import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ChevronLeft, ChevronRight, Search, CheckCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useToaster } from "../../../contexts/ToasterContext";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "../../../components/ui/breadcrumb";

// Shimmer Loading Component
const ShimmerCard = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const ShimmerText = ({ width = "w-3/4", height = "h-4" }) => (
  <ShimmerCard className={`${width} ${height} mb-2`} />
);

const BucketShimmer = () => (
  <div className="flex items-center gap-3 p-4">
    <ShimmerCard className="w-4 h-4 rounded" />
    <div className="flex-1 min-w-0">
      <ShimmerText width="w-3/4" height="h-4" />
    </div>
  </div>
);

const S3OnboardingPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToaster();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1 - Bucket Selection
  const [buckets, setBuckets] = useState([]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(true);
  
  // Normal mode pagination
  const [normalPage, setNormalPage] = useState(1);
  const [normalPerPage, setNormalPerPage] = useState(50);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    last_page: 1,
    has_next: false,
    has_previous: false
  });
  
  // Search mode pagination
  const [searchPage, setSearchPage] = useState(1);
  const [searchPerPage, setSearchPerPage] = useState(30);
  const [searchPagination, setSearchPagination] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Step 2 - Schedule Configuration
  const [repeatInterval, setRepeatInterval] = useState('1h');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [startTime, setStartTime] = useState('now');
  const [showScheduleDropdown, setShowScheduleDropdown] = useState(false);
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [customTime, setCustomTime] = useState('1h');
  const [isCreatingSchedules, setIsCreatingSchedules] = useState(false);
  const [showSummaryAccordion, setShowSummaryAccordion] = useState(false);

  // Mock S3 buckets data
  const getMockBuckets = (page = 1, per_page = 50) => {
    const mockBuckets = Array.from({ length: 100 }, (_, i) => ({
      name: `bucket-${i + 1}`,
      region: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'][i % 4],
      creationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
    }));

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedBuckets = mockBuckets.slice(start, end);
    const totalCount = mockBuckets.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedBuckets,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Fetch available S3 buckets from AWS
  const fetchBuckets = async (page = normalPage, per_page = normalPerPage) => {
    setIsLoadingBuckets(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const mockResponse = getMockBuckets(page, per_page);
      setBuckets(mockResponse.data);
      setPagination(mockResponse.pagination);
    } catch (error) {
      console.error("Error fetching S3 buckets:", error);
      showToast("Failed to fetch S3 buckets", "error");
      setBuckets([]);
    } finally {
      setIsLoadingBuckets(false);
    }
  };

  // Mock S3 bucket search results
  const getMockSearchResults = (query, page = 1, per_page = 30) => {
    const mockResults = Array.from({ length: 50 }, (_, i) => ({
      name: `${query}-bucket-${i + 1}`,
      region: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'][i % 4],
      creationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
    }));

    const start = (page - 1) * per_page;
    const end = start + per_page;
    const paginatedResults = mockResults.slice(start, end);
    const totalCount = mockResults.length;
    const lastPage = Math.ceil(totalCount / per_page);

    return {
      data: paginatedResults,
      pagination: {
        page,
        per_page,
        last_page: lastPage,
        has_next: page < lastPage,
        has_previous: page > 1,
        total_count: totalCount
      }
    };
  };

  // Search S3 buckets
  const searchS3Buckets = async (query, page = 1, per_page = 30) => {
    if (!query || query.length < 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchPagination(null);
      return;
    }

    setIsSearchLoading(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const mockResponse = getMockSearchResults(query, page, per_page);
      setSearchResults(mockResponse.data);
      setSearchPagination(mockResponse.pagination);
      setIsSearchMode(true);
    } catch (error) {
      console.error("Error searching S3 buckets:", error);
      showToast("Failed to search S3 buckets", "error");
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Initialize buckets on component mount
  useEffect(() => {
    fetchBuckets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get the buckets to display (search results or normal buckets)
  const displayedBuckets = useMemo(() => {
    const bucketsToDisplay = isSearchMode ? searchResults : buckets;
    
    // Sort buckets to move selected ones to the top
    return [...bucketsToDisplay].sort((a, b) => {
      const aSelected = selectedBuckets.some((bucket) => bucket.name === a.name);
      const bSelected = selectedBuckets.some((bucket) => bucket.name === b.name);
      
      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // If both are selected or both are unselected, maintain original order
      return 0;
    });
  }, [isSearchMode, searchResults, buckets, selectedBuckets]);

  // Handle search query changes with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchS3Buckets(searchQuery, 1, searchPerPage);
      } else if (searchQuery.trim().length === 0) {
        setIsSearchMode(false);
        setSearchResults([]);
        setSearchPagination(null);
        // Reset to first page of normal results when clearing search
        if (normalPage !== 1) {
          setNormalPage(1);
          fetchBuckets(1, normalPerPage);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bucket selection handlers
  const handleSelectBucket = (bucket) => {
    // Preserve scroll position of the list container
    const listContainer = document.querySelector('.max-h-96.overflow-y-auto.border.rounded-md');
    const scrollTop = listContainer?.scrollTop || 0;

    setSelectedBuckets((prev) => {
      const isSelected = prev.some((b) => b.name === bucket.name);
      if (isSelected) {
        return prev.filter((b) => b.name !== bucket.name);
      } else {
        return [...prev, bucket];
      }
    });

    // Restore scroll position after state update and re-render
    setTimeout(() => {
      if (listContainer) {
        listContainer.scrollTop = scrollTop;
      }
    }, 0);
  };

  // Schedule configuration helpers
  const convertIntervalToHours = (interval) => {
    const match = interval.match(/^(\d+)(m|h|d|w|mo)$/);
    if (!match) return 1;
    
    const value = parseInt(match[1]);
    const unit = match[2];
    
    switch (unit) {
      case 'm': return value / 60;
      case 'h': return value;
      case 'd': return value * 24;
      case 'w': return value * 24 * 7;
      case 'mo': return value * 24 * 30;
      default: return 1;
    }
  };

  const getCurrentHourInTimezone = (targetTimezone = timezone) => {
    const now = new Date();
    
    if (!targetTimezone) {
      return now.getHours();
    }
    
    if (targetTimezone.startsWith('UTC')) {
      const offset = targetTimezone.replace('UTC', '');
      const hours = parseInt(offset);
      const minutes = offset.includes(':') ? parseInt(offset.split(':')[1]) : 0;
      
      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const offsetTime = new Date(utcTime.getTime() + (hours * 60 + minutes) * 60000);
      
      return offsetTime.getHours();
    }
    
    try {
      const timeInTimezone = new Date(now.toLocaleString('en-US', { timeZone: targetTimezone }));
      return timeInTimezone.getHours();
    } catch (error) {
      console.error('Error getting hour in timezone:', error);
      return now.getHours();
    }
  };

  const getTimezoneDisplayName = (tz) => {
    if (!tz) {
      return 'UTC +00:00';
    }
    
    if (tz.startsWith('UTC')) {
      return tz;
    }
    if (tz === 'Asia/Kolkata') {
      return 'UTC +05:30';
    }
    return tz;
  };

  const getCurrentTimeInTimezone = () => {
    const now = new Date();
    
    if (!timezone) {
      return now.toLocaleString('en-US');
    }
    
    if (timezone.startsWith('UTC')) {
      const offset = timezone.replace('UTC', '');
      const hours = parseInt(offset);
      const minutes = offset.includes(':') ? parseInt(offset.split(':')[1]) : 0;
      
      const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const offsetTime = new Date(utcTime.getTime() + (hours * 60 + minutes) * 60000);
      
      return offsetTime.toLocaleString('en-US');
    }
    
    return now.toLocaleString('en-US', { timeZone: timezone });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showScheduleDropdown && !event.target.closest('.schedule-dropdown')) {
        setShowScheduleDropdown(false);
      }
      if (showTimezoneDropdown && !event.target.closest('.timezone-dropdown')) {
        setShowTimezoneDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showScheduleDropdown, showTimezoneDropdown]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 1 && selectedBuckets.length === 0) {
      showToast("Please select at least one bucket", "error");
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/integration/s3');
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Create bucket schedule function (mock)
  const createBucketSchedule = async (scheduleData) => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Return mock response
      return {
        success: true,
        message: 'Schedule created successfully',
        scheduleIds: Array.from({ length: scheduleData.bucketIds?.length || 0 }, (_, i) => `schedule-${Date.now()}-${i}`)
      };
    } catch (error) {
      console.error("Error creating bucket schedule:", error);
      throw error;
    }
  };

  // Add buckets to database first (mock)
  const addBucketsToDatabase = async (buckets) => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Return mock response
      return {
        success: true,
        message: 'Buckets added successfully',
        added_count: buckets.length
      };
    } catch (error) {
      console.error("Error adding buckets:", error);
      throw error;
    }
  };

  // Final submission handler
  const handleCreateSchedules = async () => {
    setIsCreatingSchedules(true);
    
    try {
      // Step 1: Add buckets to database
      console.log('Step 1: Adding buckets to database...', selectedBuckets);
      await addBucketsToDatabase(selectedBuckets);
      showToast('Buckets added successfully!', 'success');
      
      // Store bucket info for background scheduling
      const bucketInfo = {
        selectedBuckets: [...selectedBuckets],
        repeatInterval,
        customTime,
        startTime,
        timezone
      };
      
      // Navigate to main S3 page immediately
      navigate('/integration/s3');
      
      // Run scheduling in background
      scheduleBucketsInBackground(bucketInfo);
      
    } catch (error) {
      console.error("Error in handleCreateSchedules:", error);
      
      // Handle different types of errors
      if (error?.message) {
        showToast(error.message, "error");
      } else {
        showToast("Failed to add buckets", "error");
      }
    } finally {
      setIsCreatingSchedules(false);
    }
  };

  // Background scheduling function
  const scheduleBucketsInBackground = async (bucketInfo) => {
    try {
      // Wait for buckets to be processed
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 2: Fetch managed buckets to get their internal IDs (mock)
      console.log('Step 2: Fetching managed buckets to get internal IDs...');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock managed buckets
      const managedBuckets = bucketInfo.selectedBuckets.map((bucket, idx) => ({
        id: `internal-bucket-${idx + 1}`,
        name: bucket.name,
        region: bucket.region || 'us-east-1'
      }));
      console.log('Managed buckets response:', managedBuckets);
      
      // Step 3: Map selected buckets to their internal IDs
      const bucketMapping = {};
      const validBucketIds = [];
      const invalidBuckets = [];
      
      bucketInfo.selectedBuckets.forEach(selectedBucket => {
        const managedBucket = managedBuckets.find(bucket => 
          bucket.name === selectedBucket.name
        );
        
        if (managedBucket && managedBucket.id) {
          const bucketId = managedBucket.id;
          
          // Validate UUID format
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(bucketId)) {
            bucketMapping[selectedBucket.name] = bucketId;
            validBucketIds.push(bucketId);
            console.log(`Mapped ${selectedBucket.name} to ID: ${bucketId}`);
          } else {
            console.error('Invalid UUID format for bucket:', selectedBucket.name, 'ID:', bucketId);
            invalidBuckets.push({ bucketName: selectedBucket.name, error: 'Invalid bucket ID format' });
          }
        } else {
          console.error('No managed bucket found for:', selectedBucket.name);
          invalidBuckets.push({ bucketName: selectedBucket.name, error: 'Bucket not found in database' });
        }
      });
      
      if (validBucketIds.length === 0) {
        showToast('No valid buckets found for scheduling', 'error');
        return;
      }
      
      // Step 4: Create schedules using internal bucket IDs
      console.log('Step 4: Creating schedules for buckets:', validBucketIds);
      const currentHour = getCurrentHourInTimezone(bucketInfo.timezone);
      const repeatIntervalHours = convertIntervalToHours(bucketInfo.repeatInterval === 'custom' ? bucketInfo.customTime : bucketInfo.repeatInterval);
      
      const scheduleData = {
        startHour: bucketInfo.startTime === 'now' ? 0 : currentHour,
        repeatIntervalH: repeatIntervalHours,
        timezone: bucketInfo.timezone,
        bucketIds: validBucketIds
      };
      
      console.log('Schedule data:', scheduleData);
      
      const scheduleResponse = await createBucketSchedule(scheduleData);
      console.log('Schedule creation successful:', scheduleResponse);
      
      showToast(
        `Successfully scheduled ${validBucketIds.length} bucket${validBucketIds.length > 1 ? 's' : ''} for scanning!`,
        "success"
      );
      
      if (invalidBuckets.length > 0) {
        const errorMessage = `Failed to schedule ${invalidBuckets.length} bucket(s): ${invalidBuckets.map(f => f.bucketName).join(', ')}`;
        showToast(errorMessage, 'warning');
      }
      
    } catch (error) {
      console.error("Background scheduling error:", error);
      showToast('Failed to configure scan schedules. Please try again from bucket settings.', 'error');
    }
  };

  // Pagination handlers for normal mode
  const handlePrevPage = () => {
    if (pagination.has_previous) {
      const newPage = normalPage - 1;
      setNormalPage(newPage);
      fetchBuckets(newPage, normalPerPage);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      const newPage = normalPage + 1;
      setNormalPage(newPage);
      fetchBuckets(newPage, normalPerPage);
    }
  };

  const handlePerPageChange = (perPage) => {
    const perPageNum = Number(perPage);
    setNormalPerPage(perPageNum);
    setNormalPage(1);
    fetchBuckets(1, perPageNum);
  };

  // Search pagination handlers
  const handleSearchPrevPage = () => {
    if (searchPagination?.has_previous) {
      const newPage = searchPage - 1;
      setSearchPage(newPage);
      searchS3Buckets(searchQuery, newPage, searchPerPage);
    }
  };

  const handleSearchNextPage = () => {
    if (searchPagination?.has_next) {
      const newPage = searchPage + 1;
      setSearchPage(newPage);
      searchS3Buckets(searchQuery, newPage, searchPerPage);
    }
  };

  const handleSearchPerPageChange = (perPage) => {
    const perPageNum = Number(perPage);
    setSearchPerPage(perPageNum);
    setSearchPage(1);
    searchS3Buckets(searchQuery, 1, perPageNum);
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search buckets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Counter */}
            <div className="flex items-center justify-end pb-2 border-b">
              {selectedBuckets.length > 0 && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {selectedBuckets.length} bucket{selectedBuckets.length > 1 ? "s" : ""} selected
                </span>
              )}
            </div>

            {/* Buckets List */}
            <div className="max-h-96 overflow-y-auto border rounded-md">
              {isLoadingBuckets || isSearchLoading ? (
                <div className="divide-y">
                  {[...Array(8)].map((_, index) => (
                    <BucketShimmer key={index} />
                  ))}
                </div>
              ) : displayedBuckets.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  <p className="text-center">
                    {searchQuery.trim().length >= 2 ? (
                      'No buckets found matching your search.'
                    ) : searchQuery.trim() ? (
                      'Type at least 2 characters to search'
                    ) : (
                      'No buckets available.'
                    )}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {displayedBuckets.map((bucket) => {
                    const isSelected = selectedBuckets.some((b) => b.name === bucket.name);
                    return (
                      <label
                        key={bucket.name}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-purple-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectBucket(bucket)}
                          className="h-4 w-4 text-[#44087D] border-gray-300 rounded focus:ring-[#44087D] focus:ring-2"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {bucket.name}
                          </p>
                          {!isSearchMode && bucket.region && (
                            <p className="text-xs text-gray-500 truncate mt-1">
                              Region: {bucket.region}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-0 pt-4 border-t">
              {isSearchMode ? (
                // Search mode pagination
                <>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSearchPrevPage}
                      disabled={!searchPagination?.has_previous}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 mx-2">
                      Page {searchPagination?.page || 1} of {searchPagination?.last_page || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSearchNextPage}
                      disabled={!searchPagination?.has_next}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Per page:</label>
                    <select
                      value={searchPerPage}
                      onChange={(e) => handleSearchPerPageChange(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                    >
                      {[5, 10, 20, 30, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                // Normal mode pagination
                <>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={!pagination.has_previous}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 mx-2">
                      Page {pagination.page} of {pagination.last_page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={!pagination.has_next}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Per page:</label>
                    <select
                      value={pagination.per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                    >
                      {[5, 10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-sm text-gray-600 mb-4">
              Configure scan schedule for {selectedBuckets.length} bucket{selectedBuckets.length > 1 ? 's' : ''}.
            </div>

            {/* Collapsible Summary Accordion */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100"
                onClick={() => setShowSummaryAccordion(!showSummaryAccordion)}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Selected Buckets - Summary
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({selectedBuckets.length} bucket{selectedBuckets.length > 1 ? 's' : ''})
                  </span>
                </div>
                {showSummaryAccordion ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
              
              {showSummaryAccordion && (
                <div className="border-t p-4 bg-white">
                  {isCreatingSchedules ? (
                    <div className="space-y-3">
                      {[...Array(selectedBuckets.length)].map((_, index) => (
                        <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <ShimmerCard className="w-4 h-4 rounded" />
                            <div className="flex-1">
                              <ShimmerText width="w-1/2" height="h-4" />
                              <ShimmerText width="w-1/4" height="h-3" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {selectedBuckets.map((bucket) => (
                          <span
                            key={bucket.name}
                            title={bucket.region ? `Region: ${bucket.region}` : bucket.name}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                          >
                            {bucket.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule Configuration Panel */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Schedule Configuration</h3>
              </div>

              {/* Timezone Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                <div className="relative timezone-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <span className="text-gray-900">{getTimezoneDisplayName(timezone)}</span>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showTimezoneDropdown && (
                    <div className="absolute z-20 w-80 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg timezone-dropdown">
                      <div className="p-4">
                        <h4 className="text-white text-sm font-medium mb-3">Select Timezone</h4>

                        <div className="mb-3 p-2 bg-gray-700 rounded">
                          <button
                            onClick={() => {
                              setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
                              setShowTimezoneDropdown(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded text-sm transition-colors ${
                              timezone === Intl.DateTimeFormat().resolvedOptions().timeZone
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {getTimezoneDisplayName(Intl.DateTimeFormat().resolvedOptions().timeZone)} (System Default)
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {[
                            'UTC +00:00', 'UTC +01:00', 'UTC +02:00', 'UTC +03:00', 'UTC +04:00',
                            'UTC +05:00', 'UTC +05:30', 'UTC +06:00', 'UTC +06:30', 'UTC +07:00',
                            'UTC +08:00', 'UTC +08:30', 'UTC +09:00', 'UTC +10:00', 'UTC +11:00',
                            'UTC +12:00'
                          ].map((tz) => (
                            <button
                              key={tz}
                              onClick={() => {
                                setTimezone(tz);
                                setShowTimezoneDropdown(false);
                              }}
                              className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                                timezone === tz
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              {tz}
                            </button>
                          ))}
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-600">
                          <h5 className="text-white text-sm font-medium mb-2">Negative Offsets</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              'UTC -01:00', 'UTC -02:00', 'UTC -03:00', 'UTC -04:00', 'UTC -05:00',
                              'UTC -06:00', 'UTC -07:00', 'UTC -08:00', 'UTC -09:00', 'UTC -10:00',
                              'UTC -11:00', 'UTC -12:00'
                            ].map((tz) => (
                              <button
                                key={tz}
                                onClick={() => {
                                  setTimezone(tz);
                                  setShowTimezoneDropdown(false);
                                }}
                                className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                                  timezone === tz
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                              >
                                {tz}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Start Time Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                <div className="flex gap-2">
                  <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                    <input
                      type="radio"
                      name="startTime"
                      value="now"
                      checked={startTime === 'now'}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-blue-600 mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium">Now</div>
                      <div className="text-xs text-gray-500">Start immediately</div>
                    </div>
                  </label>
                  <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                    <input
                      type="radio"
                      name="startTime"
                      value="hour"
                      checked={startTime === 'hour'}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-blue-600 mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium">At {getCurrentHourInTimezone()}:00</div>
                      <div className="text-xs text-gray-500">Start at current hour</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Repeat Interval Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Repeat Every</label>
                <div className="relative schedule-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowScheduleDropdown(!showScheduleDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-900">{repeatInterval === 'custom' ? customTime : `Every ${repeatInterval}`}</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showScheduleDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg schedule-dropdown">
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-white text-sm font-medium mb-3">Common Intervals</h4>
                            <div className="space-y-2">
                              {[
                                { label: 'Every 1 hour', value: '1h' },
                                { label: 'Every 6 hours', value: '6h' },
                                { label: 'Every 1 day', value: '1d' },
                                { label: 'Every 3 days', value: '3d' },
                                { label: 'Every 1 week', value: '1w' },
                                { label: 'Every 1 month', value: '1mo' }
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => {
                                    setRepeatInterval(option.value);
                                    setShowScheduleDropdown(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                                    repeatInterval === option.value
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-300 hover:bg-gray-700'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-white text-sm font-medium mb-3">Other Intervals</h4>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { label: '3h', value: '3h' },
                                { label: '4d', value: '4d' },
                                { label: '6w', value: '6w' },
                                { label: '12h', value: '12h' },
                                { label: '10d', value: '10d' },
                                { label: '2w', value: '2w' },
                                { label: '2mo', value: '2mo' },
                                { label: 'today', value: '1d' }
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => {
                                    setRepeatInterval(option.value);
                                    setShowScheduleDropdown(false);
                                  }}
                                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                                    repeatInterval === option.value
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                >
                                  {option.value}
                                </button>
                              ))}
                            </div>

                            <div className="mt-4">
                              <h5 className="text-white text-sm font-medium mb-2">Custom</h5>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={customTime}
                                  onChange={(e) => setCustomTime(e.target.value)}
                                  placeholder="1h, 30m, 2d"
                                  className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm placeholder-gray-400"
                                />
                                <button
                                  onClick={() => {
                                    setRepeatInterval('custom');
                                    setShowScheduleDropdown(false);
                                  }}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                  Set
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule Preview */}
              <div className="bg-white border border-purple-200 rounded-md p-3">
                <div className="text-sm text-purple-800">
                  <strong>Preview:</strong> Next scan at {startTime === 'now' ? getCurrentTimeInTimezone() : `${getCurrentHourInTimezone()}:00`}, repeating every 1hr
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/integration/s3">S3</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Onboard Buckets</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold">Onboard Buckets</h1>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step
                    ? 'bg-[#44087D] border-[#44087D] text-white'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step}</span>
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step ? 'text-[#44087D]' : 'text-gray-500'
                }`}>
                  {step === 1 && 'Select Buckets'}
                  {step === 2 && 'Schedule Scans'}
                </span>
                {step < 2 && (
                  <div className={`w-8 h-0.5 mx-4 ${
                    currentStep > step ? 'bg-[#44087D]' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-white border border-gray-300 shadow-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {currentStep === 1 && 'Select Buckets to Onboard'}
              {currentStep === 2 && 'Configure Scan Schedule'}
            </CardTitle>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          <div className="flex gap-2">
            {currentStep < 2 ? (
              <Button
                onClick={handleNext}
                disabled={currentStep === 1 && selectedBuckets.length === 0}
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleCreateSchedules}
                disabled={isCreatingSchedules}
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                {isCreatingSchedules ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Schedules...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Create Schedules
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default S3OnboardingPage;
